import MicIcon from 'assets/icons/ic_mic_none.svg?react';
import CameraOutlineIcon from 'assets/icons/camera_outline.svg?react';
import WifiOutlineIcon from 'assets/icons/wifi_outline.svg?react';
import { useTranslation } from 'react-i18next';
import { Button, Tag, TagColor, TagSize } from '@spotted-zebra-uk/ui-components';
import Icon from '@/components/atoms/Icon';
import { convertExtraTimeValue } from '../../Test/Tests/helpers/extraTime';
import styles from './StepIntro.module.scss';

interface StepIntroProps {
  onContinue: () => void;
  /**
   * The interview length to quote, in minutes, already stretched by any
   * reasonable-adjustment extra time and rounded to match what the interviewer
   * says out loud. 0 when the backend has nothing to derive it from, in which
   * case no figure is shown at all — a wrong number here is worse than none,
   * since candidates plan their day around it.
   */
  estimatedDurationMinutes: number;
  /** Extra time granted, as a proportion (0.5 = 50% more). 0 when none. */
  timeModifierPercentage: number;
}

export function StepIntro({
  onContinue,
  estimatedDurationMinutes,
  timeModifierPercentage,
}: StepIntroProps) {
  const { t } = useTranslation();
  const hasExtraTime = timeModifierPercentage > 0;
  return (
    <div className={styles.stepIntro}>
      <div className={styles.content}>
        <div className={styles.topContent}>
          <h1 className={styles.title}>
            {t('aiInterview.introTitle')}
          </h1>
          <p className={styles.subtitle}>
            {t('aiInterview.introSubtitle')}
          </p>
          {estimatedDurationMinutes > 0 && (
            <div className={styles.timingTags}>
              <Tag
                isPresentational
                isDismissible={false}
                tagColor={TagColor.BLUE}
                size={TagSize.SMALL}
              >
                {t('aiInterview.estimatedTime', {
                  minutes: estimatedDurationMinutes,
                })}
              </Tag>
              {hasExtraTime && (
                <Tag
                  isPresentational
                  isDismissible={false}
                  tagColor={TagColor.BLUE}
                  size={TagSize.SMALL}
                >
                  {t('test.instructions.general.extraTime', {
                    amount: convertExtraTimeValue(timeModifierPercentage),
                  })}
                </Tag>
              )}
            </div>
          )}
        </div>

        <div className={styles.requirementsBox}>
          <div className={styles.requirementsHeader}>{t('aiInterview.requirements')}</div>
          <RequirementItem icon={<MicIcon className={styles.micIcon} />} label={t('aiInterview.microphone')} />
          <RequirementItem icon={<CameraOutlineIcon />} label={t('aiInterview.camera')} />
          <RequirementItem
            icon={<WifiOutlineIcon />}
            label={t('aiInterview.stableInternetConnection')}
          />
        </div>
      </div>

      <Button
        onClick={onContinue}
        fullWidth
        rightIcon={<Icon icon="right_arrow" />}
      >
        {t('aiInterview.letsCheckYourSetup')}
      </Button>
    </div>
  );
}

function RequirementItem({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className={styles.requirementItem}>
      <div className={styles.requirementIcon}>{icon}</div>
      <span className={styles.requirementLabel}>{label}</span>
    </div>
  );
}
